import * as Sentry from '@sentry/nuxt'
import type { ApiErrorDetails } from '~/utils/apiError'
import { isTokenizedPdfReportRoute } from '~/utils/reportRoute'

/**
 * Reports a client-side API failure to Sentry. Sentry's HTTP client
 * integration already captures 5xx responses, and an expired session is not a
 * fault, so only other 4xx failures are reported, tagged with their error code.
 * The event carries the code, status and error ID only, never user data. The
 * PDF report route is skipped: Sentry attaches the page URL, whose query holds
 * the user's access token, name and email there.
 */
export function reportApiFailure(details: ApiErrorDetails, operation: string) {
  const { statusCode, code, exceptionId } = details
  if (statusCode === undefined || statusCode < 400 || statusCode >= 500)
    return
  if (
    typeof window !== 'undefined'
    && isTokenizedPdfReportRoute(window.location.pathname, window.location.search)
  ) {
    return
  }
  if (code === 'auth_missing' || code === 'auth_invalid')
    return
  const reason = code ?? `http_${String(statusCode)}`
  Sentry.captureException(new Error(`API ${String(statusCode)} ${reason}`), {
    level: 'warning',
    tags: { failure_reason: reason, http_status: String(statusCode), operation },
    extra: { exceptionId },
  })
}
